import { Maybe } from './globals';

export const ConversationalTranslationDirection = {
  inbound: 'inbound',
  outbound: 'outbound'
} as const;

export type ConversationalTranslationDirection =
  (typeof ConversationalTranslationDirection)[keyof typeof ConversationalTranslationDirection];

export function isInbound(direction: ConversationalTranslationDirection): boolean {
  return direction === ConversationalTranslationDirection.inbound;
}

export function isOutbound(direction: ConversationalTranslationDirection): boolean {
  return direction === ConversationalTranslationDirection.outbound;
}

export const ConversationalTranslationStatus = {
  completed: 'completed',
  skipped: 'skipped',
  failed: 'failed'
} as const;

export type ConversationalTranslationStatus =
  (typeof ConversationalTranslationStatus)[keyof typeof ConversationalTranslationStatus];

/** Confidence assigned to fixed language signals (Comet check-in, warehouse country map). */
export const FIXED_LANGUAGE_SIGNAL_CONFIDENCE = 0.5;

export interface ProcessConversationalTranslationPayload {
  correlationId: string;
  pipeline: string;
  direction: ConversationalTranslationDirection;
  message: { id: string; text: string; contentHash: string };
  sender: { userId: string; languageTag: string | null; confidence: number | null };
  target: { userId: string; languageTag: string | null; confidence: number | null };
  /**
   * When false, skip cloud/history dynamic language detection and use fixed language signals only.
   * Defaults to true when omitted.
   */
  dynamicLanguageDetectionEnabled?: boolean;
  /** Optional. When set, library uses this provisioned provider for the run. */
  provider?: string | null;
}

export interface ConversationalTranslationEntry {
  targetLanguageTag: string;
  sourceLanguageTag?: string;
  sourceLanguageConfidence?: number;
  content: string;
  confidence?: number;
  provider: string;
}

export interface ConversationalTranslationResult {
  correlationId: string;
  translationStatus: ConversationalTranslationStatus;
  entries: ConversationalTranslationEntry[];
  driverLanguageTag?: string;
  driverLanguageConfidence?: number;
  driverLanguageDetectionStatus?: string;
}

/** Primary language subtag of a BCP-47 tag (`pt-BR` → `pt`). */
export function languageTagBase(languageTag: string): string {
  return languageTag.split('-')[0].toLowerCase();
}

/** True when both tags share the same primary language subtag (case-insensitive). */
export function haveSameBaseLanguage(
  languageTagA?: Maybe<string>,
  languageTagB?: Maybe<string>
): boolean {
  if (!languageTagA || !languageTagB) {
    return false;
  }
  return languageTagBase(languageTagA) === languageTagBase(languageTagB);
}

/**
 * Human-readable language name for `languageTag`, labeled in `inLocale`
 * (e.g. `en` in `es-MX` → `inglés`). Falls back to the raw tag on failure.
 */
export function languageDisplayName(languageTag: string, inLocale: string): string {
  const baseLanguage = languageTagBase(languageTag);
  try {
    return new Intl.DisplayNames([inLocale], { type: 'language' }).of(baseLanguage) ?? languageTag;
  } catch {
    return languageTag;
  }
}

/**
 * First-outbound SMS disclaimer seed. Builds an English “[Translated from {language}]”
 * phrase (language name via Intl.DisplayNames in English) for a dedicated MT call that
 * localizes the footer into the driver’s language after the message body is translated.
 */
export function translatedFromDisclaimer(sourceLanguageTag: string): string {
  const languageName = languageDisplayName(sourceLanguageTag, 'en');
  return `[Translated from ${languageName}]`;
}
